/**
 * PadEditor — the self-contained editor surface (work-slices.md § Slice B /
 * Seams it produces). Slice C composes this into the page and top bar.
 *
 * Two entry points (see exports at the bottom):
 * - **default export** (`PadEditorShell`) — the Slice C integration adapter,
 *   matching `src/components/padEditorContract.ts`
 *   (`<PadEditor pad onSaveStateChange onReady />`). This is what `PadRoute`
 *   imports. It drives the save through `src/lib/api.ts` itself.
 * - **named `PadEditor` / `PadEditorCore`** — the documented Slice B seam
 *   (`<PadEditor pad onSave onConflict renderToolbar />`) where the caller
 *   injects the save. Used by the unit tests and any caller wanting full
 *   control of the network call.
 *
 * What it owns (spec.md § Editor):
 * - The Milkdown writing surface (GFM, task lists, image-by-URL) as a single
 *   markdown document in a distraction-free centred column.
 * - The save state machine (`clean | unsaved | saving | saved | conflict`),
 *   immediate (no-debounce) save, Cmd/Ctrl+S, editor-stays-editable-during-save,
 *   and the in-flight "moved ahead → unsaved" rule.
 * - The inline 409 conflict banner and the "Reload theirs" flow (copy local
 *   body → toast → load server version).
 * - `beforeunload` guarding unsaved changes.
 *
 * What Slice C owns (NOT rendered here): the top bar, the `…` menu, routing.
 * PadEditor surfaces the controls Slice C needs via the `renderToolbar` render
 * prop (the live save state + actions) so the Save button / expiry / copy
 * actions can live in C's top bar while state ownership stays in B. The
 * standalone {@link SaveButton} and {@link ExpiryIndicator} components are also
 * exported for C to drop into the bar.
 *
 * ── Tailwind className conventions for markdown content (for Slice C) ─────────
 * Rendered markdown is styled in `milkdown.css`, scoped under
 * `.milkdown-host .milkdown`. Those styles reference only the design tokens
 * (`--color-*`, `--font-*` from styles/index.css), so Slice C does NOT need to
 * add any classes to render content correctly in either theme. The document
 * column uses: `mx-auto w-full max-w-[720px] px-6 pt-16` (720px default; swap
 * to `max-w-[860px]` for the "wide" layout per spec). Keep this column wrapper
 * if you place PadEditor inside additional chrome. Do not restyle
 * `.milkdown` descendants from the shell — extend `milkdown.css` instead so the
 * editor and any future read-only render stay consistent.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { useCallback, useEffect, useMemo, useRef, type ReactNode } from "react";
import { ConflictBanner } from "../components/ConflictBanner";
import { ToastProvider, useToast } from "../components/Toast";
import { updatePad } from "../lib/api";
import type { PadEditorProps as ShellPadEditorProps } from "../components/padEditorContract";
import { copyToClipboard } from "./clipboard";
import { MilkdownEditor, type MilkdownEditorHandle } from "./MilkdownEditor";
import type { SaveState } from "./saveState";
import type { OnConflict, OnSave, Pad, SaveResult } from "./types";
import { useBeforeUnload } from "./useBeforeUnload";
import { hasUnsavedChanges } from "./saveState";
import { useSaveMachine } from "./useSaveMachine";

const COPY_MARKDOWN_MESSAGE = "Copied markdown to clipboard";

/** Controls handed to the `renderToolbar` render prop (for Slice C's top bar). */
export interface PadEditorControls {
  /** Current save state — drive the Save button skin. */
  state: SaveState;
  /** Fire a save immediately (Save button click / programmatic). */
  save: () => void;
  /** Copy the current body as raw markdown (the `…` menu action). */
  copyMarkdown: () => Promise<void>;
}

export interface PadEditorProps {
  pad: Pad;
  /** Injected by Slice C (owns src/lib/api.ts). Resolves ok/conflict, rejects otherwise. */
  onSave: OnSave;
  /** Optional: observe a 409 (after the banner appears). */
  onConflict?: OnConflict;
  /**
   * Render prop for the top-bar controls. Slice C uses it to place the Save
   * button / copy-markdown action while PadEditor keeps state ownership.
   */
  renderToolbar?: (controls: PadEditorControls) => ReactNode;
  /** Report save-state transitions (used by the Slice C adapter / top bar). */
  onStateChange?: (state: SaveState) => void;
  /** Report the live body markdown (used by the Slice C adapter for getBody). */
  onBodyChange?: (body: string) => void;
  /** Register the `save()` trigger (used by the Slice C adapter's handle). */
  registerSave?: (save: () => void) => void;
}

export function PadEditorCore({
  pad,
  onSave,
  onConflict,
  renderToolbar,
  onStateChange,
  onBodyChange,
  registerSave,
}: PadEditorProps): ReactNode {
  const { showToast } = useToast();
  const editorRef = useRef<MilkdownEditorHandle>(null);

  const { state, conflict, serverBody, setBody, save, reloadTheirs, clearServerBody } =
    useSaveMachine({ pad, onSave, onConflict, showToast });

  // Guard tab close while there are unsaved (or conflicting) local changes.
  useBeforeUnload(hasUnsavedChanges(state));

  // Cmd/Ctrl+S saves (spec: "Cmd/Ctrl+S also saves").
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "s") {
        event.preventDefault();
        save();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [save]);

  // After a conflict is resolved, load the server version into the editor.
  useEffect(() => {
    if (serverBody !== null) {
      editorRef.current?.setMarkdown(serverBody);
      clearServerBody();
    }
  }, [serverBody, clearServerBody]);

  // Report save-state transitions upward (Slice C adapter / top bar button).
  useEffect(() => {
    onStateChange?.(state);
  }, [state, onStateChange]);

  // Register the save trigger so a parent (Slice C adapter) can fire it.
  useEffect(() => {
    registerSave?.(save);
  }, [save, registerSave]);

  // Track the latest body for copy-as-markdown without re-creating callbacks.
  const currentBodyRef = useRef(pad.body);
  const handleBodyChange = useCallback(
    (markdown: string) => {
      currentBodyRef.current = markdown;
      onBodyChange?.(markdown);
      setBody(markdown);
    },
    [setBody, onBodyChange],
  );

  const copyMarkdown = useCallback(async () => {
    try {
      // `handleBodyChange` keeps currentBodyRef in sync with the editor, so we
      // copy exactly the live markdown source.
      await copyToClipboard(currentBodyRef.current);
      showToast(COPY_MARKDOWN_MESSAGE);
    } catch {
      showToast("Couldn't save. Check your connection and try again.");
    }
  }, [showToast]);

  return (
    <div data-testid="pad-editor">
      {renderToolbar?.({ state, save, copyMarkdown })}

      {conflict ? (
        <ConflictBanner
          serverVersion={conflict.version}
          onReloadTheirs={() => {
            void reloadTheirs();
          }}
        />
      ) : null}

      <div className="mx-auto w-full max-w-[720px] px-6 pt-16">
        <MilkdownEditor ref={editorRef} defaultValue={pad.body} onChange={handleBodyChange} />
      </div>
    </div>
  );
}

/**
 * Named export kept for callers that drive the editor via the documented
 * Slice B seam (`onSave` / `onConflict` / `renderToolbar`). See {@link PadEditorCore}.
 */
export const PadEditor = PadEditorCore;

/**
 * Default export — the Slice C integration adapter.
 *
 * Slice C (already on `main`) composes the editor as
 * `<PadEditor pad onSaveStateChange onReady />` (see
 * `src/components/padEditorContract.ts`) and owns the top bar, Save button,
 * `…` menu, Cmd/Ctrl+S, and copy/delete toasts. Its `src/lib/api.ts#updatePad`
 * returns a discriminated `{ ok } | { ok: false, conflict }` result.
 *
 * This adapter bridges the documented Slice B seam to Slice C's contract so the
 * integration is the one-line import swap Slice C planned for, with NO changes
 * to Slice C's files:
 * - Builds the `onSave` callback from `updatePad`, mapping the result into the
 *   editor's `SaveResult`.
 * - Reports state via `onSaveStateChange` and registers the `{ save, getBody }`
 *   handle via `onReady`.
 * - Renders its own {@link ToastProvider} for the save-error and conflict-copy
 *   toasts (Slice C's `ShellToast` handles copy/delete; the editor owns the
 *   save-error toast per spec § Editor / Non-409 save failures).
 *
 * NOTE (surfaced to the orchestrator): work-slices.md § Slice B documents the
 * produced seam as `<PadEditor pad onSave onConflict />`, while Slice C merged a
 * different contract (`onSaveStateChange` / `onReady`). This adapter reconciles
 * the two without editing Slice C; the divergence is flagged in the PR.
 */
function PadEditorShell(props: ShellPadEditorProps): ReactNode {
  return (
    <ToastProvider>
      <PadEditorShellInner {...props} />
    </ToastProvider>
  );
}

export default PadEditorShell;

function PadEditorShellInner({ pad, onSaveStateChange, onReady }: ShellPadEditorProps): ReactNode {
  const saveRef = useRef<() => void>(() => {});
  const bodyRef = useRef<string>(pad.body);

  // Adapt Slice C's discriminated updatePad result into the editor's SaveResult.
  const onSave: OnSave = useCallback(
    async (payload): Promise<SaveResult> => {
      const result = await updatePad(pad.slug, payload);
      if (result.ok) {
        return { status: "ok", pad: result.pad };
      }
      return { status: "conflict", server: result.conflict };
    },
    [pad.slug],
  );

  // Hand the shell its imperative handle once.
  useEffect(() => {
    onReady?.({
      save: () => saveRef.current(),
      getBody: () => bodyRef.current,
    });
  }, [onReady]);

  const registerSave = useCallback((save: () => void) => {
    saveRef.current = save;
  }, []);

  const onBodyChange = useCallback((body: string) => {
    bodyRef.current = body;
  }, []);

  // Slice C owns the top bar / Save button, so no renderToolbar here.
  const props = useMemo(
    () => ({ pad, onSave, onStateChange: onSaveStateChange, onBodyChange, registerSave }),
    [pad, onSave, onSaveStateChange, onBodyChange, registerSave],
  );

  return <PadEditorCore {...props} />;
}
